import moment from 'moment';
import React, { memo } from 'react';
import { Col, Segmented, Typography } from 'components';
import { Button, Input, Row, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LoadingOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { HasAccess } from 'components/common/accessControl';
import { Filter } from 'feature/supplierTabs/priceList/filter';
import { DownloadIcon } from 'components/common/icons/DownloadIcon';
import { DeleteIcon, FilterIcon, PlusIcon } from 'components/common/icons';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { usePriceList } from './usePriceList';
import styles from './styles.module.scss';
import { DeleteModal } from './delete';
import { AddPriceListModal } from './add';
import { OnlineShop } from 'feature/supplierTabs/priceList/onlineShop';
import { OnlineShopAddEditModal } from 'feature/supplierTabs/priceList/onlineShop/addEdit';
import { BrandsDrawer } from 'feature/supplierTabs/priceList/brandsDrawer';

const { Search } = Input;

export interface SupplierPriceListDataType {
  createdDate: any;
  supplier: string;
  expireDate: any;
}

export const SupplierPriceList = memo(() => {
  const { hasAccess } = useAccessControl();
  const {
    priceList,
    handleTableChange,
    isLoading,
    pagination,
    onHandleDelete,
    onDeleteModalClose,
    deleteItem,
    onAddModalStateChange,
    openAddModal,
    filtersForm,
    applyFilters,
    resetFilters,
    onCloseFilters,
    onOpenFilters,
    openFilters,
    setSearch,
    priceListOptions,
    onSegmentedChange,
    alignValue,
    openOnlineShopModal,
    onOpenOnlineShopModalStateChange,
    onRedirectToBrand,
    onExpand,
    brandsData,
    drawerState,
  } = usePriceList();

  const columns: ColumnsType<string> = [
    {
      title: 'Brands',
      dataIndex: 'brands',
      key: 'brands',
      render: (brands) => {
        return (
          <Row align={'middle'} wrap={false} justify={'space-between'}>
            {!!brands?.length && (
              <Typography.Paragraph
                underline
                type={'secondary'}
                className={styles.brand_column}
                ellipsis={{ tooltip: true }}
                title={brands[0]?.name}
                onClick={() => onRedirectToBrand(brands[0].id)}
              >
                {brands[0]?.name}
              </Typography.Paragraph>
            )}
            {brands?.length > 1 && (
              <Button
                size={'small'}
                type={'text'}
                variant={'text'}
                style={{ fontSize: '14px', color: '#525866', backgroundColor: 'transparent', fontWeight: 400 }}
                onClick={() => onExpand(brands)}
              >
                <Typography.Text underline type={'secondary'}>
                  +{brands?.length - 1}
                </Typography.Text>
              </Button>
            )}
          </Row>
        );
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'createDate',
      key: 'createDate',
      sorter: true,
      render: (value) => moment.utc(value).format('DD.MM.YYYY'),
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expireDate',
      key: 'expireDate',
      sorter: true,
      render: (value) => moment.utc(value).format('DD.MM.YYYY'),
    },
    {
      title: 'File Name',
      key: 'fileName',
      sorter: true,
      width: 200,
      ellipsis: true,
      render: ({ file, fileName }) => {
        return file ? (
          <Button
            type="link"
            icon={<DownloadIcon />}
            download
            target="_blank"
            className={styles.btn_download}
            title={fileName}
            rel="noopener noreferrer"
            href={file}
          >
            <span className={styles.btn_download_text}>{fileName || 'Not Set'}</span>
          </Button>
        ) : (
          ''
        );
      },
    },
  ];

  if (hasAccess(PERMISSIONS.suppliers.price_list.delete)) {
    columns.push({
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Row justify={'center'} className={'bg-white'}>
          <HasAccess role={PERMISSIONS.suppliers.price_list.delete}>
            <Button type={'link'} icon={<DeleteIcon />} onClick={() => onHandleDelete(value)} />
          </HasAccess>
        </Row>
      ),
    });
  }

  return (
    <div className={styles.main}>
      <Row gutter={[0, 24]} justify={'space-between'}>
        <Col>
          <Segmented
            value={alignValue}
            style={{ marginBottom: 32 }}
            onChange={onSegmentedChange}
            options={priceListOptions as any}
          />
        </Col>
        <Col>
          <Row align={'middle'} justify={'end'}>
            <HasAccess
              role={
                alignValue === 'Pricelists'
                  ? PERMISSIONS.suppliers.price_list.write
                  : PERMISSIONS.suppliers.online_shop.write
              }
            >
              <Button
                type={'primary'}
                onClick={alignValue === 'Pricelists' ? onAddModalStateChange : onOpenOnlineShopModalStateChange}
                icon={<PlusIcon />}
              >
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        {alignValue === 'Pricelists' && (
          <Col span={24} className={styles.main_container}>
            <Row>
              <Col span={24}>
                <Row justify={'end'} className={styles.actions_container}>
                  <Col style={{ width: '300px' }}>
                    <Search enterButton={false} placeholder={'Search'} onChange={(e) => setSearch(e.target.value)} />
                  </Col>
                  <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
                </Row>
              </Col>
              <Col span={24} style={{ paddingTop: '20px' }}>
                <HasAccess role={PERMISSIONS.suppliers.price_list.read}>
                  <Table
                    bordered
                    size={'small'}
                    columns={columns}
                    dataSource={priceList?.content || []}
                    rowKey="id"
                    loading={{
                      indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                      spinning: isLoading,
                    }}
                    locale={
                      isLoading
                        ? { emptyText: null } // Hide "No Data" text while loading
                        : {} // Use default AntD "No Data" icon when empty
                    }
                    pagination={{
                      position: ['bottomCenter'],
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} items`,
                      current: pagination.page,
                      pageSize: pagination.size,
                      total: priceList?.total || 0,
                      size: 'default',
                    }}
                    onChange={handleTableChange as any}
                  />
                </HasAccess>
              </Col>
            </Row>
          </Col>
        )}
        {alignValue === 'Online Shop' && (
          <Col span={24}>
            <OnlineShop />
          </Col>
        )}
      </Row>
      <DeleteModal openModal={!!deleteItem} onDeleteModalClose={onDeleteModalClose} dataToDelete={deleteItem} />
      <AddPriceListModal openModal={openAddModal} onModalStateChange={onAddModalStateChange} />
      <OnlineShopAddEditModal modalState={openOnlineShopModal} onModalStateChange={onOpenOnlineShopModalStateChange} />
      <Filter
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <BrandsDrawer drawerState={drawerState} onExpand={onExpand} data={brandsData} />
    </div>
  );
});
